import { gql } from 'apollo-boost';
import { useApolloClient } from '@apollo/client';
import FILTER_TYPE from '../util/messageType';

const CHECKBOX_PATCH_FRAGMENT = gql`
    fragment CheckboxPatchMessage on Message {
        id
        isStarred
        isCheckbox
        isChecked
        folder {
            id
            title
            owner
            parent
            deletedAt
            __typename
        }
    }
`;

const isRefForMessage = (readField, ref, id) => readField('id', ref) === id;
const getFolderTotalCount = (readField, entry, currentMessages = []) => {
    const totalCount = Number(readField('totalCount', entry));
    return Number.isFinite(totalCount) ? totalCount : currentMessages.length;
};

const useUpdateCheckboxes = () => {
    const client = useApolloClient();

    const patchMessageEntity = message => {
        if (!message?.id) {
            return;
        }

        try {
            const cacheId = client.cache.identify({
                __typename: 'Message',
                id: message.id
            });

            if (!cacheId) {
                return;
            }

            client.cache.writeFragment({
                id: cacheId,
                fragment: CHECKBOX_PATCH_FRAGMENT,
                data: {
                    __typename: 'Message',
                    id: message.id,
                    isStarred: message.isStarred,
                    isCheckbox: message.isCheckbox,
                    isChecked: message.isChecked,
                    folder: message.folder
                        ? {
                              ...message.folder,
                              __typename: message.folder.__typename || 'Folder'
                          }
                        : null
                }
            });
        } catch (error) {
            // Entity might not be normalized yet; ignore and continue with list update.
        }
    };

    const addToCheckboxGlobal = message => {
        if (!message?.id || !message?.folder?.id) {
            return;
        }

        patchMessageEntity(message);

        client.cache.modify({
            id: 'ROOT_QUERY',
            fields: {
                getCheckboxMessagesGlobal(existingFolders = [], { readField, toReference }) {
                    const folderId = message.folder.id;
                    const messageRef = toReference({
                        __typename: 'Message',
                        id: message.id
                    });
                    const folderRef =
                        toReference({
                            __typename: 'Folder',
                            id: folderId
                        }) || {
                            __typename: message.folder.__typename || 'Folder',
                            ...message.folder
                        };

                    let foundFolder = false;
                    const updatedFolders = existingFolders.map(entry => {
                        const entryFolder = readField('folder', entry);
                        const entryFolderId = entryFolder ? readField('id', entryFolder) : undefined;
                        if (entryFolderId !== folderId) {
                            return entry;
                        }

                        foundFolder = true;
                        const currentMessages = readField('messages', entry) || [];
                        const currentTotalCount = getFolderTotalCount(readField, entry, currentMessages);
                        const alreadyExists = currentMessages.some(ref =>
                            isRefForMessage(readField, ref, message.id)
                        );

                        if (alreadyExists) {
                            return entry;
                        }

                        return {
                            __typename: readField('__typename', entry) || 'FolderContentGlobal',
                            folder: entryFolder || folderRef,
                            messages: [messageRef, ...currentMessages],
                            totalCount: currentTotalCount + 1
                        };
                    });

                    if (foundFolder) {
                        return updatedFolders;
                    }

                    return [
                        {
                            __typename: 'FolderContentGlobal',
                            folder: folderRef,
                            messages: [messageRef],
                            totalCount: 1
                        },
                        ...updatedFolders
                    ];
                }
            }
        });
    };

    const addToCheckboxFilters = message => {
        if (!message?.id || !message?.folder?.id) {
            return;
        }

        patchMessageEntity(message);

        client.cache.modify({
            id: 'ROOT_QUERY',
            fields: {
                filterMessages(existing = {}, { args, readField, toReference }) {
                    const filterData = args?.data || {};
                    const filterTypes = Array.isArray(filterData.type) ? filterData.type : [];
                    const filterText = String(filterData.text || '').trim();
                    const filterFolderId = String(filterData.folderId || '');
                    const isTrash = Boolean(filterData.isTrash);

                    if (!filterTypes.includes(FILTER_TYPE.CHECK) || isTrash || filterText) {
                        return existing;
                    }

                    if (filterFolderId && filterFolderId !== String(message.folder.id)) {
                        return existing;
                    }

                    const messageRef = toReference({
                        __typename: 'Message',
                        id: message.id
                    });

                    if (!messageRef) {
                        return existing;
                    }

                    const currentMessages = Array.isArray(existing?.messages) ? existing.messages : [];
                    const alreadyExists = currentMessages.some(ref =>
                        isRefForMessage(readField, ref, message.id)
                    );

                    if (alreadyExists) {
                        return existing;
                    }

                    return {
                        ...existing,
                        messages: [messageRef, ...currentMessages]
                    };
                }
            }
        });
    };

    const removeFromCheckboxGlobal = message => {
        if (!message?.id || !message?.folder?.id) {
            return;
        }

        patchMessageEntity(message);

        client.cache.modify({
            id: 'ROOT_QUERY',
            fields: {
                getCheckboxMessagesGlobal(existingFolders = [], { readField }) {
                    const folderId = message.folder.id;
                    const nextFolders = [];

                    existingFolders.forEach(entry => {
                        const entryFolder = readField('folder', entry);
                        const entryFolderId = entryFolder ? readField('id', entryFolder) : undefined;

                        if (entryFolderId !== folderId) {
                            nextFolders.push(entry);
                            return;
                        }

                        const currentMessages = readField('messages', entry) || [];
                        const currentTotalCount = getFolderTotalCount(readField, entry, currentMessages);
                        const filteredMessages = currentMessages.filter(
                            ref => !isRefForMessage(readField, ref, message.id)
                        );
                        const removedCount = currentMessages.length - filteredMessages.length;

                        if (filteredMessages.length > 0) {
                            nextFolders.push({
                                __typename: readField('__typename', entry) || 'FolderContentGlobal',
                                folder: entryFolder,
                                messages: filteredMessages,
                                totalCount: Math.max(currentTotalCount - removedCount, filteredMessages.length)
                            });
                        }
                    });

                    return nextFolders;
                }
            }
        });
    };

    const removeFromCheckboxFilters = message => {
        if (!message?.id || !message?.folder?.id) {
            return;
        }

        patchMessageEntity(message);

        client.cache.modify({
            id: 'ROOT_QUERY',
            fields: {
                filterMessages(existing = {}, { args, readField }) {
                    const filterData = args?.data || {};
                    const filterTypes = Array.isArray(filterData.type) ? filterData.type : [];
                    const filterFolderId = String(filterData.folderId || '');
                    const isTrash = Boolean(filterData.isTrash);

                    if (!filterTypes.includes(FILTER_TYPE.CHECK) || isTrash) {
                        return existing;
                    }

                    if (filterFolderId && filterFolderId !== String(message.folder.id)) {
                        return existing;
                    }

                    const currentMessages = Array.isArray(existing?.messages) ? existing.messages : [];
                    const filteredMessages = currentMessages.filter(
                        ref => !isRefForMessage(readField, ref, message.id)
                    );

                    if (filteredMessages.length === currentMessages.length) {
                        return existing;
                    }

                    return {
                        ...existing,
                        messages: filteredMessages
                    };
                }
            }
        });
    };

    const updateCheckboxes = messages => {
        try {
            messages.forEach(message => {
                addToCheckboxGlobal(message);
                addToCheckboxFilters(message);
            });
        } catch (error) {
            console.log('Error while updating checkbox messages', error);
        }
    };

    const spliceFromCheckboxes = messages => {
        try {
            messages.forEach(message => {
                removeFromCheckboxGlobal(message);
                removeFromCheckboxFilters(message);
            });
        } catch (error) {
            console.log('Error while removing checkbox messages', error);
        }
    };

    return { updateCheckboxes, spliceFromCheckboxes };
};

export default useUpdateCheckboxes;
